import axios from "axios";
import { officialTcgCards } from "../data/site";
import { getLinkedTag, getLocalPoints, overrideLocalPoints, spendLocalPoints } from "./rewards";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const client = axios.create({ baseURL: API, withCredentials: true });
const DECK_KEY = "dolo-tcg-deck";
const COLLECTION_KEY = "dolo-tcg-collection";

export const DECK_SIZE = 6;

export const cardById = (id) => officialTcgCards.find((card) => card.id === id);

/* House-issued loaner pool — always slottable so anyone can battle. The Chaos
   Engine riot terrain rides along so every runner can flip a crowd. The rest
   of the catalog must be pulled from crates before it can enter a deck. */
export const LOANER_IDS = ["net-warden", "infantry", "scavenger", "kaelen", "zion", "dolt", "sector-9-chaos-engine"];
export const STARTER_DECK = ["net-warden", "infantry", "kaelen", "zion", "dolt", "sector-9-chaos-engine"];

export const isSlottable = (id, collection) => LOANER_IDS.includes(id) || (collection?.[id] || 0) > 0;

export const isDeckLegal = (deck, collection) =>
  deck.length === DECK_SIZE && deck.every((id) => isSlottable(id, collection));

const NET_POOL = officialTcgCards.filter((card) => card.faction === "BLACK-OPS NET");

export const getLocalDeck = () => {
  try {
    const ids = JSON.parse(localStorage.getItem(DECK_KEY)) || [];
    const valid = ids.filter((id) => cardById(id));
    return valid.length === DECK_SIZE ? valid : null;
  } catch {
    return null;
  }
};

export const saveLocalDeck = (ids) => localStorage.setItem(DECK_KEY, JSON.stringify(ids));

export const fetchCloudDeck = async () => {
  const { data } = await client.get("/tcg/deck");
  return data;
};

export const saveCloudDeck = async (cards) => {
  const { data } = await client.put("/tcg/deck", { cards });
  return data;
};

export const postBattleRecord = async (outcome, roundsWon) => {
  const { data } = await client.post("/tcg/battle", { outcome, rounds_won: roundsWon });
  return data;
};

export const fetchLeaderboard = async () => {
  const { data } = await client.get("/tcg/leaderboard");
  return data;
};

const statValue = (value) => (value === "∞" ? 99 : Number(value) || 0);

/* raw power printed on the frame: attack + hull + the scrap you sank into it */
export const cardPower = (card) =>
  statValue(card.stats.atk) + statValue(card.stats.def) + statValue(card.stats.scrap);

export const rollDie = (sides = 8) => 1 + Math.floor(Math.random() * sides);

/* NET war council draw: fodder up front, apex monsters close the show —
   every slot a unique unit */
export const buildEnemyDeck = () => {
  const fodder = NET_POOL.filter((c) => c.rarity !== "legendary").sort(() => Math.random() - 0.5);
  const legendaries = NET_POOL.filter((c) => c.rarity === "legendary").sort(() => Math.random() - 0.5);
  const deck = [...fodder.slice(0, 2), ...legendaries.slice(0, 4)];
  return deck.sort((a, b) => cardPower(a) - cardPower(b));
};

/* 5 PTS per round taken, +15 for the battle, +5 for a flawless sweep, plus any
   salvage scraped mid-fight — capped at 50 */
export const battlePoints = (roundsWon, won, flawless, bonus = 0) =>
  Math.min(50, roundsWon * 5 + (won ? 15 : 0) + (flawless ? 5 : 0) + bonus);

/* apex threats worth a feat when beaten in a round */
export const isApexCard = (card) => statValue(card.stats.scrap) >= 15;

/* ---------- printed abilities, wired for real ---------- */
export const ABILITIES = {
  kaelen: { name: "WORKSHOP PATCH", text: "If it falls, your next unit enters at +6." },
  zion: { name: "I AM THE TECH", text: "+2 power for each round you have taken." },
  orion: { name: "SHADOW-STEP", text: "Steals the enemy's surveillance edge." },
  drion: { name: "ORBITAL STRIKE", text: "+8 power in rounds 4-6." },
  valkyrie: { name: "AERIAL JUDGMENT", text: "Its surveillance edge is doubled to +8." },
  "net-commander": { name: "NEURAL STRATEGIST", text: "Reveals the council's next unit for the rest of the battle." },
  "net-warden": { name: "FIREWALL", text: "The enemy's roll is capped at 4." },
  phantom: { name: "SPECTER PROTOCOL", text: "A lost clash is rolled again, once." },
  infantry: { name: "LEGION", text: "+3 power for each round you have dropped." },
  dolt: { name: "KINETIC SPARK", text: "+3 power per consecutive round taken." },
  kushiro: { name: "COMMAND OVERRIDE", text: "Once per battle: the enemy fights at -10." },
  scavenger: { name: "SCRAP SALVAGE", text: "Winning the round banks +2 bonus PTS." },
  vanguard: { name: "RECLAIMED MIGHT", text: "Immune to all enemy abilities." },
  echo: { name: "FINAL STAND", text: "+15 power when it is your last unit." },
  volo: { name: "NULLIFICATION FIELD", text: "The enemy's ability is silenced." },
  singularity: { name: "SINGULARITY COLLAPSE", text: "If it wins, your next unit fights at -10." },
  omnicron: { name: "NET-MATRIX WEAVING", text: "+3 power for every round already played." },
  "cyber-tyrant": { name: "SYSTEM PURGE", text: "Enemy edge cut and roll capped at 4." },
  architect: { name: "VOID-NET WEAVE", text: "Reality warps at its location — the enemy fights at -9. Its surveillance edge cannot be cut." },
  "cyber-queen": { name: "NET-MATRIX COLLAPSE", text: "The matrix folds shut — the enemy loses its ability and fights at -6." },
  abyss: { name: "NULL-FIELD", text: "A permanent null-field blooms at its location — the enemy fights at -5." },
  "cyber-ape": { name: "CYBER-RAGE", text: "+4 power while its side trails on rounds — rage builds as it bleeds." },
  "omnicron-nexus": { name: "ABSOLUTE SINGULARITY", text: "An absolute null-field blooms — the enemy's surveillance edge is stripped and they fight at -7." },
  "singularity-zero-point": { name: "ZERO-POINT DETONATION", text: "If it falls, the zero-point field detonates — the enemy's next unit fights at -7." },
  "nexus-omnicron": { name: "TOTAL CONSENSUS", text: "Dissent is obsolete — the enemy's ability is silenced and they fight at -4." },
  circe: { name: "NEURO-WARP", text: "Steals the enemy unit's ability." },
  dolo: { name: "THERMAL LASER SWEEP", text: "Enemy DEF counts as half. Immune to enemy tricks." },
  deacon: { name: "WASTELAND COMMAND", text: "Win or fall, your next unit enters at +4." },
  "funk-master": { name: "FUNKADELIC BEAT", text: "+3 power for each round you have taken." },
  goji: { name: "TENTACLE LURE", text: "The enemy fights at -6 power." },
  "dark-dolo": { name: "CIRCUIT OVERLOAD", text: "Once per battle: the enemy's die reads zero." },
  kai: { name: "SALVAGE SAVANT", text: "+2 power for every round already played." },
  jonah: { name: "POINT-BLANK CROSSBOW", text: "Pierces the enemy's surveillance edge." },
  sarah: { name: "SILENT ADVANCE", text: "Carried debuffs don't touch her." },
  kishuu: { name: "BEAT DROP", text: "Her die can't be capped, zeroed, or re-rolled by the enemy." },
  kenjiro: { name: "PHANTOM DRIBBLE", text: "His die rolls double." },
  "gorge-claw": { name: "RENDING CLAWS", text: "If it wins the clash, the enemy's next unit fights at -4." },
  "sinew-walker": { name: "POUNCE STRIKE", text: "+4 power while its side trails on rounds." },
  "ash-skin": { name: "WASTELAND RESILIENCE", text: "If dice lose it the clash, raw power decides instead." },
  "energy-eater": { name: "ENERGY SAP", text: "Drains the enemy: +6 power, enemy -6." },
  "sand-worm": { name: "BURROWING MENACE", text: "Buries the enemy's surveillance edge. If it falls, lava blood burns their next unit at -4." },
  luna: { name: "SONIC STUN", text: "If she wins, the enemy's next unit is stunned at -6." },
  "malfunction-7": { name: "UNSTABLE RESONANCE", text: "+2 power for every card left in your hand." },
  vex: { name: "EMP BLAST", text: "+6 power against Black-Ops Net units." },
  osteowarrior: { name: "OVERCLOCK", text: "Its die rolls double — but the strain hits your next unit at -3." },
  "metal-witch": { name: "CURSED CIRCUITS", text: "If it falls, the attacker's next unit malfunctions at -5." },
  "cyborg-shogun": { name: "BLOOD-DRENCHED BLADE", text: "+1 power for each round its side has taken, up to +3." },
  riff: { name: "BATTLE HYMN", text: "+2 power this clash, and your next unit enters at +3." },
  koji: { name: "PRIMAL FURY", text: "+6 power while its side trails by 2 or more rounds." },
  "sniper-elite": { name: "TARGET LOCK", text: "+4 power against enemies carrying a debuff." },
  "core-runner": { name: "CORE DELIVERY", text: "If she falls, the package arrives: your next unit +3." },
  "heavy-enforcer": { name: "PIERCING ORDINANCE", text: "Pierces 6 of the enemy's DEF." },
  goliath: { name: "GOLIATH'S PRESENCE", text: "Carried debuffs slide off; +3 power against debuffed enemies." },
  elara: { name: "APEX NETWORK", text: "Her surveillance edge doubles and the enemy's edge is cut." },
  "white-guard-bastion": { name: "AUTOMATED DEFENSE GRID", text: "Its turret grid pings the enemy for 4 unblockable — they fight at -4." },
  "command-bastion": { name: "SURVEILLANCE BEACON", text: "Its surveillance edge is doubled to +8." },
  "sector-9-point": { name: "SCRAP STORAGE", text: "Winning the round banks +3 bonus PTS." },
  "sector-defense-bastion": { name: "BLAST DOORS", text: "+10 power against Mutant units." },
  "goliath-hauler": { name: "MOBILE COMMAND PLATFORM", text: "If it wins, your next unit enters at +4." },
  "mutated-brute": { name: "TOXIC TARGET LOCK", text: "If it wins, the enemy's next unit is poisoned at -4." },
  "siege-mutant": { name: "CHIMERA RESILIENCE", text: "Carried debuffs slide off its plated hide." },
  "chronos-chimera": { name: "SURVEILLANCE NULL-FIELD", text: "The enemy's surveillance edge is nullified." },
  "assimilation-abomination": { name: "ASSIMILATION WEB", text: "+6 power; non-Mutant enemies fight at -6." },
  "riotball-final": { name: "AL RIOTBALL DYNAMICS", text: "Its raw power mirrors the enemy's — the crowd decides the rest." },
  "white-guard-reinforcement": { name: "AEGIS WALL", text: "The barrier holds — the enemy's roll is capped at 4." },
  "red-force-pitch": { name: "RED FORCE MOMENTUM", text: "+2 power per consecutive round its side has taken." },
  "overcharged-core": { name: "OVERCHARGED CORE", text: "Its die rolls double — but if it falls, its own next unit burns at -4." },
  "scrapper-elite-technician": { name: "PLASMA BREACH", text: "Pierces 4 of the enemy's DEF, and the enemy fights at -2." },
  "sector-9-lockdown": { name: "ENVIRONMENTAL LOCK", text: "Enemies with Surveillance under 3 are swept — they fight at -6." },
  ironclad: { name: "IRONCLAD PROTOCOL", text: "+3 power. Carried debuffs slide off." },
  "scavenger-elite": { name: "SCAVENGER INSTINCTS", text: "+1 power for every round already played." },
  "field-commander": { name: "ASSAULT TACTICS", text: "+2 power this clash, and your next unit enters at +3." },
  "city-runner": { name: "SALVAGE OP", text: "Winning the round banks +2 bonus PTS." },
  "assimilation-lord": { name: "ASSIMILATION NETWORK", text: "+5 power; non-Mutant enemies fight at -5." },
  "data-vanguard": { name: "UPLINK SWEEP", text: "Cuts the enemy's surveillance edge and reveals their next unit." },
  "vanguard-scrapper-elite": { name: "REINFORCED PLATING", text: "+2 power. Carried debuffs are halved." },
  "data-elite": { name: "NETWORK HUB", text: "+2 power for every card left in your hand." },
  "bio-assimilator": { name: "TECH SIPHON", text: "Drains the enemy: +3 power, enemy -3." },
  "eye-clusters": { name: "ALL-SEEING", text: "Its surveillance edge cannot be cut, stolen, or buried." },
  "spine-chimera": { name: "REACTIVE SPINES", text: "+3 power, and the enemy fights at -2." },
  "rust-eater": { name: "CORROSIVE TOUCH", text: "Corrodes the enemy's plating — they fight at -6." },
  "spine-chimera-assault": { name: "SPIKE VOLLEY", text: "+2 power. If it wins, the enemy's next unit fights at -2." },
  "shadow-kind": { name: "AMBUSH PREDATOR", text: "+5 power in the opening rounds — it strikes from the dark." },
  "scale-hide": { name: "REPTILIAN HIDE", text: "Hardened scales: +4 power." },
  "mutted-chatterer": { name: "SENSORY FEED", text: "It reads the field — the enemy's next unit is revealed." },
  "blight-bringer": { name: "DEATH BURST", text: "If it falls, the enemy's next unit is infected at -5." },
  "mutted-chatterer-internal": { name: "CONFLICTING WHISPERS", text: "Feeds the enemy false signals — they fight at -4." },
  "omega-7": { name: "LAST STAND", text: "If it falls, its attacker takes the full recoil — the enemy's next unit fights at -8." },
  "eye-clusters-ocular": { name: "SPECTRAL GAZE", text: "+4 power while its side trails on rounds — it saw the strike coming." },
  "eye-clusters-psionic": { name: "PSIONIC SIGHT", text: "Nothing stays hidden — the enemy's surveillance edge is stripped." },
  "mutted-chatterer-cortex": { name: "ECHO SCREAM", text: "A psychic scream tears at the enemy — they fight at -5." },
  "kane-ironhide": { name: "SURVEILLANCE NULL-FIELD", text: "Untouchable — carried debuffs slide off, and the enemy's surveillance edge is nullified." },
  "chimera-lord-vance": { name: "CHIMERA HIVE-MIND", text: "+2 power for every round already played — the hive grows." },
  "eliza-patch": { name: "EMERGENCY PROTOCOL", text: "When she falls, your next unit enters at +5 — patched up and angry." },
  "maeve-glitch": { name: "SYSTEM OVERCLOCK", text: "If she wins, your next unit enters at +3." },
  "jax-surge": { name: "OVERLOAD PROTOCOL", text: "+4 power — but if he falls, your next unit is drained at -3." },
  "kara-raven": { name: "GRAPPLE DEPLOY", text: "Her grapple drone feeds the field — she takes the surveillance edge even on a tie." },
  "zoe-spark": { name: "ENERGY FEED", text: "+2 power, and her jolt stings — the enemy fights at -2." },
  "elias-scrap": { name: "SCAVENGER'S INSTINCT", text: "Winning the round banks +2 bonus PTS." },
  "kenji-shift": { name: "SYSTEM OVERCLOCK", text: "+3 power. If he wins, your next unit enters at +2." },
  "eliza-maw": { name: "DRONE DEPLOYMENT", text: "Her drone feeds the field — +6 power while she holds the surveillance edge." },
  "caleb-forge": { name: "SCAVENGED SALVAGE", text: "Winning the round banks +1 bonus PTS for every round already played." },
  "lyra-shadow": { name: "SCOUT'S EYE", text: "Her drone marks the prey — the enemy's next unit is revealed and fights at -2." },
};

export const abilityOf = (card) => ABILITIES[card.id] || null;

/* variant printings — same unit, different pressings; collectors need the tag */
const displayNameOf = (card) => card.name.split(",")[0];
const NAME_TALLY = officialTcgCards.reduce((tally, card) => {
  const key = displayNameOf(card);
  tally[key] = (tally[key] || 0) + 1;
  return tally;
}, {});
export const variantOf = (card) => (NAME_TALLY[displayNameOf(card)] > 1 ? card.variant || card.cardNo : null);

/* set chase — how many printings of a shared-name unit the runner owns */
const NAME_PRINTINGS = officialTcgCards.reduce((map, card) => {
  (map[displayNameOf(card)] = map[displayNameOf(card)] || []).push(card.id);
  return map;
}, {});
export const setProgressOf = (card, collection) => {
  const printings = NAME_PRINTINGS[displayNameOf(card)];
  if (!printings || printings.length < 2) return null;
  return { owned: printings.filter((id) => (collection?.[id] || 0) > 0).length, total: printings.length };
};

/* Resolve one clash with abilities live. ctx carries the running battle state:
   roundIndex (0-based), youWon/youLost/netWon/netLost, youMomentum/netMomentum,
   youCardsLeft/netCardsLeft (after this play), youPending/netPending (deferred
   modifiers), kushiroYouUsed/kushiroNetUsed, playerRoll/enemyRoll.
   Returns totals, winner, modifier feed for the UI, and flags for deferred
   effects the caller must store (patch = kaelen, collapse = singularity). */
export const resolveRound = (playerCard, enemyCard, ctx) => {
  const mods = [];
  const flags = {};
  let pEdge = statValue(playerCard.stats.surveillance) > statValue(enemyCard.stats.surveillance) ? 4 : 0;
  let eEdge = statValue(enemyCard.stats.surveillance) > statValue(playerCard.stats.surveillance) ? 4 : 0;

  /* the shadow-wing's grapple drone wins her the edge even on a dead tie */
  const pAbility0 = abilityOf(playerCard) ? playerCard.id : null;
  const eAbility0 = abilityOf(enemyCard) ? enemyCard.id : null;
  if (pEdge === 0 && eEdge === 0 && statValue(playerCard.stats.surveillance) === statValue(enemyCard.stats.surveillance)) {
    if (pAbility0 === "kara-raven" && eAbility0 !== "kara-raven") {
      pEdge = 4;
      mods.push({ side: "you", label: "GRAPPLE DEPLOY — tie turned into her edge" });
    } else if (eAbility0 === "kara-raven" && pAbility0 !== "kara-raven") {
      eEdge = 4;
      mods.push({ side: "net", label: "GRAPPLE DEPLOY — tie turned into their edge" });
    }
  }
  let pRoll = ctx.playerRoll;
  let eRoll = ctx.enemyRoll;
  let pBonus = ctx.youPending || 0;
  let eBonus = ctx.netPending || 0;
  if (ctx.youPending) mods.push({ side: "you", label: `CARRIED CHARGE ${ctx.youPending > 0 ? "+" : ""}${ctx.youPending}` });
  if (ctx.netPending) mods.push({ side: "net", label: `CARRIED CHARGE ${ctx.netPending > 0 ? "+" : ""}${ctx.netPending}` });

  const pImmune = playerCard.id === "vanguard" || playerCard.id === "dolo";
  const eImmune = enemyCard.id === "vanguard" || enemyCard.id === "dolo";
  const immuneLabel = (id) => (id === "dolo" ? "UNSTOPPABLE MOMENTUM" : "RECLAIMED MIGHT");

  let pAbility = abilityOf(playerCard) ? playerCard.id : null;
  let eAbility = abilityOf(enemyCard) ? enemyCard.id : null;

  /* immunity — the enemy ability bounces off before it fires */
  if (pImmune && eAbility) {
    mods.push({ side: "you", label: `${immuneLabel(playerCard.id)} — enemy ability bounces off` });
    eAbility = null;
  }
  if (eImmune && pAbility) {
    mods.push({ side: "net", label: `${immuneLabel(enemyCard.id)} — your ability bounces off` });
    pAbility = null;
  }

  /* volo silences before anything else fires */
  if (eAbility === "volo" && pAbility) {
    mods.push({ side: "net", label: "NULLIFICATION FIELD — your ability silenced" });
    pAbility = null;
  }
  if (pAbility === "volo" && eAbility) {
    mods.push({ side: "you", label: "NULLIFICATION FIELD — enemy ability silenced" });
    eAbility = null;
  }

  /* the cyber-queen's collapse folds the matrix shut — the enemy's ability is lost */
  if (eAbility === "cyber-queen" && pAbility) {
    mods.push({ side: "net", label: "NET-MATRIX COLLAPSE — your ability silenced" });
    pAbility = null;
  }
  if (pAbility === "cyber-queen" && eAbility) {
    mods.push({ side: "you", label: "NET-MATRIX COLLAPSE — enemy ability silenced" });
    eAbility = null;
  }

  /* the collective will tolerates no dissent — the enemy's ability is nullified */
  if (eAbility === "nexus-omnicron" && pAbility) {
    mods.push({ side: "net", label: "TOTAL CONSENSUS — your ability silenced" });
    pAbility = null;
  }
  if (pAbility === "nexus-omnicron" && eAbility) {
    mods.push({ side: "you", label: "TOTAL CONSENSUS — enemy ability silenced" });
    eAbility = null;
  }

  /* circe steals whatever is left across the table */
  if (pAbility === "circe" && eAbility) {
    mods.push({ side: "you", label: `NEURO-WARP — stole ${ABILITIES[eAbility].name}` });
    pAbility = eAbility;
    eAbility = null;
  } else if (eAbility === "circe" && pAbility) {
    mods.push({ side: "net", label: `NEURO-WARP — stole ${ABILITIES[pAbility].name}` });
    eAbility = pAbility;
    pAbility = null;
  }

  /* sarah advances untouched by carried debuffs */
  if (pAbility === "sarah" && pBonus < 0) {
    pBonus = 0;
    mods.push({ side: "you", label: "SILENT ADVANCE — carried debuff shrugged off" });
  }
  if (eAbility === "sarah" && eBonus < 0) {
    eBonus = 0;
    mods.push({ side: "net", label: "SILENT ADVANCE — carried debuff shrugged off" });
  }

  /* goliath cannot be pushed — carried debuffs slide off */
  if (pAbility === "goliath" && pBonus < 0) {
    pBonus = 0;
    mods.push({ side: "you", label: "GOLIATH'S PRESENCE — carried debuff ignored" });
  }
  if (eAbility === "goliath" && eBonus < 0) {
    eBonus = 0;
    mods.push({ side: "net", label: "GOLIATH'S PRESENCE — carried debuff ignored" });
  }

  /* the siege mutant's plated hide shrugs off carried debuffs */
  if (pAbility === "siege-mutant" && pBonus < 0) {
    pBonus = 0;
    mods.push({ side: "you", label: "CHIMERA RESILIENCE — carried debuff ignored" });
  }
  if (eAbility === "siege-mutant" && eBonus < 0) {
    eBonus = 0;
    mods.push({ side: "net", label: "CHIMERA RESILIENCE — carried debuff ignored" });
  }

  /* ironclad cannot be moved — carried debuffs slide off */
  if (pAbility === "ironclad" && pBonus < 0) {
    pBonus = 0;
    mods.push({ side: "you", label: "IRONCLAD PROTOCOL — carried debuff ignored" });
  }
  if (eAbility === "ironclad" && eBonus < 0) {
    eBonus = 0;
    mods.push({ side: "net", label: "IRONCLAD PROTOCOL — carried debuff ignored" });
  }

  /* reinforced plating halves whatever debuff gets through */
  if (pAbility === "vanguard-scrapper-elite" && pBonus < 0) {
    pBonus = Math.ceil(pBonus / 2);
    mods.push({ side: "you", label: "REINFORCED PLATING — carried debuff halved" });
  }
  if (eAbility === "vanguard-scrapper-elite" && eBonus < 0) {
    eBonus = Math.ceil(eBonus / 2);
    mods.push({ side: "net", label: "REINFORCED PLATING — carried debuff halved" });
  }

  /* kane's null-field makes him untouchable — carried debuffs slide off */
  if (pAbility === "kane-ironhide" && pBonus < 0) {
    pBonus = 0;
    mods.push({ side: "you", label: "SURVEILLANCE NULL-FIELD — carried debuff ignored" });
  }
  if (eAbility === "kane-ironhide" && eBonus < 0) {
    eBonus = 0;
    mods.push({ side: "net", label: "SURVEILLANCE NULL-FIELD — carried debuff ignored" });
  }

  /* kishuu's beat drop — her die is untouchable */
  const pDiceGuarded = pAbility === "kishuu";
  const eDiceGuarded = eAbility === "kishuu";

  /* surveillance edges — doubled, stolen, or purged */
  if (pAbility === "valkyrie" && pEdge > 0) {
    pEdge = 8;
    mods.push({ side: "you", label: "AERIAL JUDGMENT — edge doubled" });
  }
  if (eAbility === "valkyrie" && eEdge > 0) {
    eEdge = 8;
    mods.push({ side: "net", label: "AERIAL JUDGMENT — edge doubled" });
  }
  if (pAbility === "orion" && eEdge > 0) {
    pEdge += eEdge;
    eEdge = 0;
    mods.push({ side: "you", label: "SHADOW-STEP — edge stolen" });
  }
  if (eAbility === "orion" && pEdge > 0) {
    eEdge += pEdge;
    pEdge = 0;
    mods.push({ side: "net", label: "SHADOW-STEP — edge stolen" });
  }

  /* elara's network overrides everything on the field */
  if (pAbility === "elara" && (pEdge > 0 || eEdge > 0)) {
    if (pEdge > 0) pEdge = 8;
    if (eEdge > 0) eEdge = 0;
    mods.push({ side: "you", label: "APEX NETWORK — her edge doubled, enemy edge cut" });
  }
  if (eAbility === "elara" && (pEdge > 0 || eEdge > 0)) {
    if (eEdge > 0) eEdge = 8;
    if (pEdge > 0) pEdge = 0;
    mods.push({ side: "net", label: "APEX NETWORK — their edge doubled, your edge cut" });
  }

  /* the command bastion's beacon doubles its own edge */
  if (pAbility === "command-bastion" && pEdge > 0) {
    pEdge = 8;
    mods.push({ side: "you", label: "SURVEILLANCE BEACON — edge doubled" });
  }
  if (eAbility === "command-bastion" && eEdge > 0) {
    eEdge = 8;
    mods.push({ side: "net", label: "SURVEILLANCE BEACON — edge doubled" });
  }

  /* chronos-chimera's null-field erases the enemy's edge entirely */
  if (pAbility === "chronos-chimera" && eEdge > 0) {
    eEdge = 0;
    mods.push({ side: "you", label: "SURVEILLANCE NULL-FIELD — enemy edge nullified" });
  }
  if (eAbility === "chronos-chimera" && pEdge > 0) {
    pEdge = 0;
    mods.push({ side: "net", label: "SURVEILLANCE NULL-FIELD — your edge nullified" });
  }

  /* the psionic cluster sees through every veil — nothing stays hidden */
  if (pAbility === "eye-clusters-psionic" && eEdge > 0) {
    eEdge = 0;
    mods.push({ side: "you", label: "PSIONIC SIGHT — enemy edge stripped" });
  }
  if (eAbility === "eye-clusters-psionic" && pEdge > 0) {
    pEdge = 0;
    mods.push({ side: "net", label: "PSIONIC SIGHT — your edge stripped" });
  }

  /* the absolute singularity zeroes every signal at its location */
  if (pAbility === "omnicron-nexus" && eEdge > 0) {
    eEdge = 0;
    mods.push({ side: "you", label: "ABSOLUTE SINGULARITY — enemy edge zeroed" });
  }
  if (eAbility === "omnicron-nexus" && pEdge > 0) {
    pEdge = 0;
    mods.push({ side: "net", label: "ABSOLUTE SINGULARITY — your edge zeroed" });
  }

  /* the data vanguard cuts the enemy's eyes off the network */
  if (pAbility === "data-vanguard" && eEdge > 0) {
    eEdge = 0;
    mods.push({ side: "you", label: "UPLINK SWEEP — enemy edge cut" });
  }
  if (eAbility === "data-vanguard" && pEdge > 0) {
    pEdge = 0;
    mods.push({ side: "net", label: "UPLINK SWEEP — your edge cut" });
  }

  /* kane's null-field blinds the enemy's surveillance */
  if (pAbility === "kane-ironhide" && eEdge > 0) {
    eEdge = 0;
    mods.push({ side: "you", label: "SURVEILLANCE NULL-FIELD — enemy edge nullified" });
  }
  if (eAbility === "kane-ironhide" && pEdge > 0) {
    pEdge = 0;
    mods.push({ side: "net", label: "SURVEILLANCE NULL-FIELD — your edge nullified" });
  }

  /* jonah's crossbow pierces the enemy's eyes */
  if (pAbility === "jonah" && eEdge > 0) {
    eEdge = 0;
    mods.push({ side: "you", label: "POINT-BLANK CROSSBOW — enemy edge pierced" });
  }
  if (eAbility === "jonah" && pEdge > 0) {
    pEdge = 0;
    mods.push({ side: "net", label: "POINT-BLANK CROSSBOW — your edge pierced" });
  }

  /* the worm buries the enemy's eyes entirely */
  if (pAbility === "sand-worm" && eEdge > 0) {
    eEdge = 0;
    mods.push({ side: "you", label: "BURROWING MENACE — enemy edge buried" });
  }
  if (eAbility === "sand-worm" && pEdge > 0) {
    pEdge = 0;
    mods.push({ side: "net", label: "BURROWING MENACE — your edge buried" });
  }
  if (pAbility === "cyber-tyrant") {
    if (eEdge > 0 || eRoll > 4) mods.push({ side: "you", label: eDiceGuarded ? "SYSTEM PURGE — enemy edge cut" : "SYSTEM PURGE — enemy edge cut, roll capped" });
    eEdge = 0;
    if (!eDiceGuarded) eRoll = Math.min(eRoll, 4);
    else mods.push({ side: "net", label: "BEAT DROP — purge can't touch the die" });
  }
  if (eAbility === "cyber-tyrant") {
    if (pEdge > 0 || pRoll > 4) mods.push({ side: "net", label: pDiceGuarded ? "SYSTEM PURGE — your edge cut" : "SYSTEM PURGE — your edge cut, roll capped" });
    pEdge = 0;
    if (!pDiceGuarded) pRoll = Math.min(pRoll, 4);
    else mods.push({ side: "you", label: "BEAT DROP — purge can't touch the die" });
  }
  if (pAbility === "net-warden" && eRoll > 4 && !eDiceGuarded) {
    eRoll = 4;
    mods.push({ side: "you", label: "FIREWALL — enemy roll capped" });
  }
  if (eAbility === "net-warden" && pRoll > 4 && !pDiceGuarded) {
    pRoll = 4;
    mods.push({ side: "net", label: "FIREWALL — your roll capped" });
  }

  /* the white guard barrier holds the line — enemy roll capped */
  if (pAbility === "white-guard-reinforcement" && eRoll > 4 && !eDiceGuarded) {
    eRoll = 4;
    mods.push({ side: "you", label: "AEGIS WALL — enemy roll capped" });
  }
  if (eAbility === "white-guard-reinforcement" && pRoll > 4 && !pDiceGuarded) {
    pRoll = 4;
    mods.push({ side: "net", label: "AEGIS WALL — your roll capped" });
  }

  /* the eye-clusters cannot be blinded — their edge always reforms */
  if (pAbility === "eye-clusters" && pEdge === 0 && statValue(playerCard.stats.surveillance) > statValue(enemyCard.stats.surveillance)) {
    pEdge = 4;
    mods.push({ side: "you", label: "ALL-SEEING — its gaze can't be cut" });
  }
  if (eAbility === "eye-clusters" && eEdge === 0 && statValue(enemyCard.stats.surveillance) > statValue(playerCard.stats.surveillance)) {
    eEdge = 4;
    mods.push({ side: "net", label: "ALL-SEEING — its gaze can't be cut" });
  }

  /* void-net protocols — the architect cannot be corrupted; its edge always reforms */
  if (pAbility === "architect" && pEdge === 0 && statValue(playerCard.stats.surveillance) > statValue(enemyCard.stats.surveillance)) {
    pEdge = 4;
    mods.push({ side: "you", label: "VOID-NET PROTOCOLS — its edge can't be cut" });
  }
  if (eAbility === "architect" && eEdge === 0 && statValue(enemyCard.stats.surveillance) > statValue(playerCard.stats.surveillance)) {
    eEdge = 4;
    mods.push({ side: "net", label: "VOID-NET PROTOCOLS — its edge can't be cut" });
  }

  /* straight power grants */
  const abilityPower = (id, side) => {
    const won = side === "you" ? ctx.youWon : ctx.netWon;
    const lost = side === "you" ? ctx.youLost : ctx.netLost;
    const momentum = side === "you" ? ctx.youMomentum : ctx.netMomentum;
    const cardsLeft = side === "you" ? ctx.youCardsLeft : ctx.netCardsLeft;
    switch (id) {
      case "zion":
        return won > 0 ? { amount: won * 2, label: `I AM THE TECH +${won * 2}` } : null;
      case "funk-master":
        return won > 0 ? { amount: won * 3, label: `FUNKADELIC BEAT +${won * 3}` } : null;
      case "drion":
        return ctx.roundIndex >= 3 ? { amount: 8, label: "ORBITAL STRIKE +8" } : null;
      case "dolt":
        return momentum > 0 ? { amount: momentum * 3, label: `KINETIC SPARK +${momentum * 3}` } : null;
      case "infantry":
        return lost > 0 ? { amount: lost * 3, label: `LEGION +${lost * 3}` } : null;
      case "echo":
        return cardsLeft === 0 ? { amount: 15, label: "FINAL STAND +15" } : null;
      case "omnicron":
        return ctx.roundIndex > 0 ? { amount: ctx.roundIndex * 3, label: `NET-MATRIX WEAVING +${ctx.roundIndex * 3}` } : null;
      case "kai":
        return ctx.roundIndex > 0 ? { amount: ctx.roundIndex * 2, label: `SALVAGE SAVANT +${ctx.roundIndex * 2}` } : null;
      case "sinew-walker":
        return lost > won ? { amount: 4, label: "POUNCE STRIKE +4 — trailing prey" } : null;
      case "eye-clusters-ocular":
        return lost > won ? { amount: 4, label: "SPECTRAL GAZE +4 — it saw the strike coming" } : null;
      case "cyber-ape":
        return lost > won ? { amount: 4, label: "CYBER-RAGE +4 — rage builds as it bleeds" } : null;
      case "malfunction-7":
        return cardsLeft > 0 ? { amount: cardsLeft * 2, label: `UNSTABLE RESONANCE +${cardsLeft * 2}` } : null;
      case "cyborg-shogun":
        return won > 0 ? { amount: Math.min(3, won), label: `BLOOD-DRENCHED BLADE +${Math.min(3, won)}` } : null;
      case "koji":
        return lost - won >= 2 ? { amount: 6, label: "PRIMAL FURY +6 — cornered" } : null;
      case "sniper-elite": {
        const marked = side === "you" ? ctx.netPending : ctx.youPending;
        return (marked || 0) < 0 ? { amount: 4, label: "TARGET LOCK +4 — enemy marked" } : null;
      }
      case "goliath": {
        const marked = side === "you" ? ctx.netPending : ctx.youPending;
        return (marked || 0) < 0 ? { amount: 3, label: "BIG GAME HUNTER +3 — enemy marked" } : null;
      }
      case "red-force-pitch":
        return momentum > 0 ? { amount: momentum * 2, label: `RED FORCE MOMENTUM +${momentum * 2}` } : null;
      case "scavenger-elite":
        return ctx.roundIndex > 0 ? { amount: ctx.roundIndex, label: `SCAVENGER INSTINCTS +${ctx.roundIndex}` } : null;
      case "field-commander":
        return { amount: 2, label: "ASSAULT TACTICS +2" };
      case "ironclad":
        return { amount: 3, label: "IRONCLAD PROTOCOL +3" };
      case "vanguard-scrapper-elite":
        return { amount: 2, label: "REINFORCED PLATING +2" };
      case "data-elite":
        return cardsLeft > 0 ? { amount: cardsLeft * 2, label: `NETWORK HUB +${cardsLeft * 2}` } : null;
      case "spine-chimera":
        return { amount: 3, label: "REACTIVE SPINES +3" };
      case "spine-chimera-assault":
        return { amount: 2, label: "SPIKE VOLLEY +2" };
      case "shadow-kind":
        return ctx.roundIndex < 2 ? { amount: 5, label: "AMBUSH PREDATOR +5 — struck from the dark" } : null;
      case "scale-hide":
        return { amount: 4, label: "REPTILIAN HIDE +4" };
      case "chimera-lord-vance":
        return ctx.roundIndex > 0 ? { amount: ctx.roundIndex * 2, label: `CHIMERA HIVE-MIND +${ctx.roundIndex * 2}` } : null;
      case "jax-surge":
        return { amount: 4, label: "OVERLOAD PROTOCOL +4" };
      case "zoe-spark":
        return { amount: 2, label: "ENERGY FEED +2" };
      case "kenji-shift":
        return { amount: 3, label: "SYSTEM OVERCLOCK +3" };
      case "net-commander":
        return side === "net" ? { amount: 4, label: "NEURAL STRATEGIST +4" } : null;
      default:
        return null;
    }
  };
  const pGrant = pAbility && abilityPower(pAbility, "you");
  if (pGrant) {
    pBonus += pGrant.amount;
    mods.push({ side: "you", label: pGrant.label });
  }
  const eGrant = eAbility && abilityPower(eAbility, "net");
  if (eGrant) {
    eBonus += eGrant.amount;
    mods.push({ side: "net", label: eGrant.label });
  }

  /* kushiro's once-per-battle override */
  if (pAbility === "kushiro" && !ctx.kushiroYouUsed) {
    eBonus -= 10;
    flags.kushiroYou = true;
    mods.push({ side: "you", label: "COMMAND OVERRIDE — enemy -10" });
  }
  if (eAbility === "kushiro" && !ctx.kushiroNetUsed) {
    pBonus -= 10;
    flags.kushiroNet = true;
    mods.push({ side: "net", label: "COMMAND OVERRIDE — you fight at -10" });
  }

  /* dark-dolo — once per battle the enemy die reads zero */
  if (pAbility === "dark-dolo" && !ctx.circuitYouUsed) {
    if (eDiceGuarded) {
      mods.push({ side: "net", label: "BEAT DROP — circuit overload shrugged off" });
    } else {
      eRoll = 0;
      flags.circuitYou = true;
      mods.push({ side: "you", label: "CIRCUIT OVERLOAD — enemy die reads 0" });
    }
  }
  if (eAbility === "dark-dolo" && !ctx.circuitNetUsed) {
    if (pDiceGuarded) {
      mods.push({ side: "you", label: "BEAT DROP — circuit overload shrugged off" });
    } else {
      pRoll = 0;
      flags.circuitNet = true;
      mods.push({ side: "net", label: "CIRCUIT OVERLOAD — your die reads 0" });
    }
  }

  /* goji drags the whole table down */
  if (pAbility === "goji") {
    eBonus -= 6;
    mods.push({ side: "you", label: "TENTACLE LURE — enemy -6" });
  }
  if (eAbility === "goji") {
    pBonus -= 6;
    mods.push({ side: "net", label: "TENTACLE LURE — you fight at -6" });
  }

  /* energy eater drains the enemy's charge */
  if (pAbility === "energy-eater") {
    pBonus += 6;
    eBonus -= 6;
    mods.push({ side: "you", label: "ENERGY SAP — +6, enemy -6" });
  }
  if (eAbility === "energy-eater") {
    eBonus += 6;
    pBonus -= 6;
    mods.push({ side: "net", label: "ENERGY SAP — their +6, your -6" });
  }

  /* vex shorts Net hardware on contact */
  if (pAbility === "vex" && enemyCard.faction === "BLACK-OPS NET") {
    pBonus += 6;
    mods.push({ side: "you", label: "EMP BLAST +6 — Net unit shorted" });
  }
  if (eAbility === "vex" && playerCard.faction === "BLACK-OPS NET") {
    eBonus += 6;
    mods.push({ side: "net", label: "EMP BLAST +6 — Net unit shorted" });
  }

  /* the bastion's defense grid pings anything that closes in */
  if (pAbility === "white-guard-bastion") {
    eBonus -= 4;
    mods.push({ side: "you", label: "AUTOMATED DEFENSE GRID — enemy takes 4 unblockable" });
  }
  if (eAbility === "white-guard-bastion") {
    pBonus -= 4;
    mods.push({ side: "net", label: "AUTOMATED DEFENSE GRID — you take 4 unblockable" });
  }

  /* blast doors — mutant claws glance off the bastion */
  if (pAbility === "sector-defense-bastion" && enemyCard.faction === "MUTANT") {
    pBonus += 10;
    mods.push({ side: "you", label: "BLAST DOORS +10 — mutant assault repelled" });
  }
  if (eAbility === "sector-defense-bastion" && playerCard.faction === "MUTANT") {
    eBonus += 10;
    mods.push({ side: "net", label: "BLAST DOORS +10 — mutant assault repelled" });
  }

  /* the abomination drags non-chimera units into the web */
  if (pAbility === "assimilation-abomination") {
    pBonus += 6;
    if (enemyCard.faction !== "MUTANT") {
      eBonus -= 6;
      mods.push({ side: "you", label: "ASSIMILATION WEB — +6, enemy -6" });
    } else {
      mods.push({ side: "you", label: "ASSIMILATION WEB +6 — chimera kin untouched" });
    }
  }
  if (eAbility === "assimilation-abomination") {
    eBonus += 6;
    if (playerCard.faction !== "MUTANT") {
      pBonus -= 6;
      mods.push({ side: "net", label: "ASSIMILATION WEB — their +6, your -6" });
    } else {
      mods.push({ side: "net", label: "ASSIMILATION WEB +6 — chimera kin untouched" });
    }
  }

  /* the final matchup mirrors the enemy's raw power */
  if (pAbility === "riotball-final") {
    const mirror = cardPower(enemyCard) - cardPower(playerCard);
    if (mirror !== 0) {
      pBonus += mirror;
      mods.push({ side: "you", label: `AL RIOTBALL DYNAMICS — power mirrored ${mirror > 0 ? "+" : ""}${mirror}` });
    }
  }
  if (eAbility === "riotball-final") {
    const mirror = cardPower(playerCard) - cardPower(enemyCard);
    if (mirror !== 0) {
      eBonus += mirror;
      mods.push({ side: "net", label: `AL RIOTBALL DYNAMICS — power mirrored ${mirror > 0 ? "+" : ""}${mirror}` });
    }
  }

  /* plasma breach melts plating and scalds the frame */
  if (pAbility === "scrapper-elite-technician") {
    eBonus -= Math.min(4, statValue(enemyCard.stats.def)) + 2;
    mods.push({ side: "you", label: "PLASMA BREACH — 4 DEF pierced, enemy -2" });
  }
  if (eAbility === "scrapper-elite-technician") {
    pBonus -= Math.min(4, statValue(playerCard.stats.def)) + 2;
    mods.push({ side: "net", label: "PLASMA BREACH — 4 DEF pierced, your -2" });
  }

  /* sector 9's lockdown sweeps anything without eyes */
  if (pAbility === "sector-9-lockdown" && statValue(enemyCard.stats.surveillance) < 3) {
    eBonus -= 6;
    mods.push({ side: "you", label: "ENVIRONMENTAL LOCK — enemy swept -6" });
  }
  if (eAbility === "sector-9-lockdown" && statValue(playerCard.stats.surveillance) < 3) {
    pBonus -= 6;
    mods.push({ side: "net", label: "ENVIRONMENTAL LOCK — you are swept -6" });
  }

  /* the assimilation lord feeds on anything that isn't chimera */
  if (pAbility === "assimilation-lord") {
    pBonus += 5;
    if (enemyCard.faction !== "MUTANT") {
      eBonus -= 5;
      mods.push({ side: "you", label: "ASSIMILATION NETWORK — +5, enemy -5" });
    } else {
      mods.push({ side: "you", label: "ASSIMILATION NETWORK +5 — chimera kin untouched" });
    }
  }
  if (eAbility === "assimilation-lord") {
    eBonus += 5;
    if (playerCard.faction !== "MUTANT") {
      pBonus -= 5;
      mods.push({ side: "net", label: "ASSIMILATION NETWORK — their +5, your -5" });
    } else {
      mods.push({ side: "net", label: "ASSIMILATION NETWORK +5 — chimera kin untouched" });
    }
  }

  /* the bio-assimilator siphons the enemy's systems */
  if (pAbility === "bio-assimilator") {
    pBonus += 3;
    eBonus -= 3;
    mods.push({ side: "you", label: "TECH SIPHON — +3, enemy -3" });
  }
  if (eAbility === "bio-assimilator") {
    eBonus += 3;
    pBonus -= 3;
    mods.push({ side: "net", label: "TECH SIPHON — their +3, your -3" });
  }

  /* reactive spines punish every approach */
  if (pAbility === "spine-chimera") {
    eBonus -= 2;
    mods.push({ side: "you", label: "REACTIVE SPINES — enemy -2" });
  }
  if (eAbility === "spine-chimera") {
    pBonus -= 2;
    mods.push({ side: "net", label: "REACTIVE SPINES — you fight at -2" });
  }

  /* the rust-eater corrodes whatever it touches */
  if (pAbility === "rust-eater") {
    eBonus -= 6;
    mods.push({ side: "you", label: "CORROSIVE TOUCH — enemy plating corroded -6" });
  }
  if (eAbility === "rust-eater") {
    pBonus -= 6;
    mods.push({ side: "net", label: "CORROSIVE TOUCH — your plating corroded -6" });
  }

  /* the chatterer's whispers misdirect the enemy's strike */
  if (pAbility === "mutted-chatterer-internal") {
    eBonus -= 4;
    mods.push({ side: "you", label: "CONFLICTING WHISPERS — enemy misdirected -4" });
  }
  if (eAbility === "mutted-chatterer-internal") {
    pBonus -= 4;
    mods.push({ side: "net", label: "CONFLICTING WHISPERS — you are misdirected -4" });
  }

  /* the cortex's echo scream tears straight through the enemy's skull */
  if (pAbility === "mutted-chatterer-cortex") {
    eBonus -= 5;
    mods.push({ side: "you", label: "ECHO SCREAM — enemy psychically torn -5" });
  }
  if (eAbility === "mutted-chatterer-cortex") {
    pBonus -= 5;
    mods.push({ side: "net", label: "ECHO SCREAM — you are psychically torn -5" });
  }

  /* the architect's void-net warps reality — enemy stats collapse toward 1 */
  if (pAbility === "architect") {
    eBonus -= 9;
    mods.push({ side: "you", label: "VOID-NET WEAVE — enemy stats collapse -9" });
  }
  if (eAbility === "architect") {
    pBonus -= 9;
    mods.push({ side: "net", label: "VOID-NET WEAVE — your stats collapse -9" });
  }

  /* the matrix folds shut around the enemy unit */
  if (pAbility === "cyber-queen") {
    eBonus -= 6;
    mods.push({ side: "you", label: "NET-MATRIX COLLAPSE — enemy crushed -6" });
  }
  if (eAbility === "cyber-queen") {
    pBonus -= 6;
    mods.push({ side: "net", label: "NET-MATRIX COLLAPSE — you are crushed -6" });
  }

  /* abyss blooms a permanent null-field at its location */
  if (pAbility === "abyss") {
    eBonus -= 5;
    mods.push({ side: "you", label: "NULL-FIELD — enemy nulled -5" });
  }
  if (eAbility === "abyss") {
    pBonus -= 5;
    mods.push({ side: "net", label: "NULL-FIELD — you are nulled -5" });
  }

  /* the absolute null-field grinds enemy stats toward zero */
  if (pAbility === "omnicron-nexus") {
    eBonus -= 7;
    mods.push({ side: "you", label: "ABSOLUTE SINGULARITY — enemy zeroed -7" });
  }
  if (eAbility === "omnicron-nexus") {
    pBonus -= 7;
    mods.push({ side: "net", label: "ABSOLUTE SINGULARITY — you are zeroed -7" });
  }

  /* the collective will absorbs the enemy unit */
  if (pAbility === "nexus-omnicron") {
    eBonus -= 4;
    mods.push({ side: "you", label: "TOTAL CONSENSUS — enemy assimilated -4" });
  }
  if (eAbility === "nexus-omnicron") {
    pBonus -= 4;
    mods.push({ side: "net", label: "TOTAL CONSENSUS — you are assimilated -4" });
  }

  /* the tech-ghoul's jolt stings on contact */
  if (pAbility === "zoe-spark") {
    eBonus -= 2;
    mods.push({ side: "you", label: "ENERGY FEED — enemy jolted -2" });
  }
  if (eAbility === "zoe-spark") {
    pBonus -= 2;
    mods.push({ side: "net", label: "ENERGY FEED — you are jolted -2" });
  }

  /* the scavenger queen's drone turns her edge into firepower */
  if (pAbility === "eliza-maw" && pEdge > 0) {
    pBonus += 6;
    mods.push({ side: "you", label: "DRONE DEPLOYMENT +6 — edge feed" });
  }
  if (eAbility === "eliza-maw" && eEdge > 0) {
    eBonus += 6;
    mods.push({ side: "net", label: "DRONE DEPLOYMENT +6 — edge feed" });
  }

  /* riff's hymn carries the whole line */
  if (pAbility === "riff") {
    pBonus += 2;
    flags.hymnYou = 3;
    mods.push({ side: "you", label: "BATTLE HYMN — +2, your next unit +3" });
  }
  if (eAbility === "riff") {
    eBonus += 2;
    flags.hymnNet = 3;
    mods.push({ side: "net", label: "BATTLE HYMN — their +2, next unit +3" });
  }

  /* dolo's boombox laser bypasses hull plating */
  if (pAbility === "dolo") {
    eBonus -= Math.floor(statValue(enemyCard.stats.def) / 2);
    mods.push({ side: "you", label: "THERMAL LASER SWEEP — enemy DEF halved" });
  }
  if (eAbility === "dolo") {
    pBonus -= Math.floor(statValue(playerCard.stats.def) / 2);
    mods.push({ side: "net", label: "THERMAL LASER SWEEP — your DEF halved" });
  }

  /* heavy enforcer pierces straight through plating */
  if (pAbility === "heavy-enforcer") {
    eBonus -= Math.min(6, statValue(enemyCard.stats.def));
    mods.push({ side: "you", label: "PIERCING ORDINANCE — 6 DEF pierced" });
  }
  if (eAbility === "heavy-enforcer") {
    pBonus -= Math.min(6, statValue(playerCard.stats.def));
    mods.push({ side: "net", label: "PIERCING ORDINANCE — 6 DEF pierced" });
  }

  /* kenjiro doubles his die — unblockable pace */
  if (pAbility === "kenjiro") {
    pRoll *= 2;
    mods.push({ side: "you", label: `PHANTOM DRIBBLE — die doubled to ${pRoll}` });
  }
  if (eAbility === "kenjiro") {
    eRoll *= 2;
    mods.push({ side: "net", label: `PHANTOM DRIBBLE — die doubled to ${eRoll}` });
  }

  /* osteowarrior overclocks its die — the strain carries forward */
  if (pAbility === "osteowarrior") {
    pRoll *= 2;
    flags.strainYou = -3;
    mods.push({ side: "you", label: `OVERCLOCK — die doubled to ${pRoll}, strain -3 to your next unit` });
  }
  if (eAbility === "osteowarrior") {
    eRoll *= 2;
    flags.strainNet = -3;
    mods.push({ side: "net", label: `OVERCLOCK — die doubled to ${eRoll}, strain -3 to their next unit` });
  }

  /* the overcharged core surges its die — unstable power */
  if (pAbility === "overcharged-core") {
    pRoll *= 2;
    mods.push({ side: "you", label: `OVERCHARGED CORE — die surges to ${pRoll}` });
  }
  if (eAbility === "overcharged-core") {
    eRoll *= 2;
    mods.push({ side: "net", label: `OVERCHARGED CORE — die surges to ${eRoll}` });
  }

  const total = (side, roll) => cardPower(side === "you" ? playerCard : enemyCard) + (side === "you" ? pEdge + pBonus : eEdge + eBonus) + roll;
  let pTotal = total("you", pRoll);
  let eTotal = total("net", eRoll);

  /* phantom re-rolls a losing clash once — unless the beat drop denies it */
  if (pAbility === "phantom" && pTotal < eTotal) {
    if (eDiceGuarded) {
      mods.push({ side: "net", label: "BEAT DROP — re-roll denied" });
    } else {
      pRoll = rollDie();
      pTotal = total("you", pRoll);
      mods.push({ side: "you", label: "SPECTER PROTOCOL — clash re-rolled" });
    }
  }
  if (eAbility === "phantom" && eTotal < pTotal) {
    if (pDiceGuarded) {
      mods.push({ side: "you", label: "BEAT DROP — re-roll denied" });
    } else {
      eRoll = rollDie();
      eTotal = total("net", eRoll);
      mods.push({ side: "net", label: "SPECTER PROTOCOL — clash re-rolled" });
    }
  }

  const playerWinsRaw = pTotal >= eTotal;
  let playerWins = playerWinsRaw;

  /* ash-skin resilience — if dice lose it the clash, raw power decides */
  const rawCompare = () =>
    cardPower(playerCard) + pEdge + pBonus >= cardPower(enemyCard) + eEdge + eBonus;
  if (pAbility === "ash-skin" && !playerWins && rawCompare()) {
    playerWins = true;
    mods.push({ side: "you", label: "WASTELAND RESILIENCE — dice stripped, raw power stands" });
  } else if (eAbility === "ash-skin" && playerWins && !rawCompare()) {
    playerWins = false;
    mods.push({ side: "net", label: "WASTELAND RESILIENCE — dice stripped, raw power stands" });
  }

  /* aftermath — deferred effects the caller stores for the next round */
  if (pAbility === "kaelen" && !playerWins) {
    flags.patchYou = 6;
    mods.push({ side: "you", label: "WORKSHOP PATCH — your next unit +6" });
  }
  if (eAbility === "kaelen" && playerWins) flags.patchNet = 6;
  if (pAbility === "scavenger" && playerWins) {
    flags.salvage = 2;
    mods.push({ side: "you", label: "SCRAP SALVAGE — +2 bonus PTS" });
  }
  if (eAbility === "singularity" && !playerWins) {
    flags.collapseYou = -10;
    mods.push({ side: "net", label: "SINGULARITY COLLAPSE — your next unit -10" });
  }
  if (pAbility === "singularity" && playerWins) flags.collapseNet = -10;
  if (pAbility === "net-commander") {
    flags.peekYou = true;
    mods.push({ side: "you", label: "NEURAL STRATEGIST — next unit revealed" });
  }

  /* deacon commands the line forward — win or fall */
  if (pAbility === "deacon") {
    flags.commandYou = 4;
    mods.push({ side: "you", label: "WASTELAND COMMAND — your next unit +4" });
  }
  if (eAbility === "deacon") {
    flags.commandNet = 4;
    mods.push({ side: "net", label: "WASTELAND COMMAND — their next unit +4" });
  }

  /* gorge-claw rends the survivor's armor */
  if (pAbility === "gorge-claw" && playerWins) {
    flags.rendNet = -4;
    mods.push({ side: "you", label: "RENDING CLAWS — their next unit -4" });
  }
  if (eAbility === "gorge-claw" && !playerWins) {
    flags.rendYou = -4;
    mods.push({ side: "net", label: "RENDING CLAWS — your next unit -4" });
  }

  /* luna's sonic stun locks the next unit down */
  if (pAbility === "luna" && playerWins) {
    flags.stunNet = -6;
    mods.push({ side: "you", label: "SONIC STUN — their next unit -6" });
  }
  if (eAbility === "luna" && !playerWins) {
    flags.stunYou = -6;
    mods.push({ side: "net", label: "SONIC STUN — your next unit -6" });
  }

  /* lava blood scalds whatever brought the worm down */
  if (pAbility === "sand-worm" && !playerWins) {
    flags.burnNet = -4;
    mods.push({ side: "you", label: "LAVA BLOOD — their next unit burns -4" });
  }
  if (eAbility === "sand-worm" && playerWins) {
    flags.burnYou = -4;
    mods.push({ side: "net", label: "LAVA BLOOD — your next unit burns -4" });
  }

  /* the witch's killer carries the malfunction forward */
  if (pAbility === "metal-witch" && !playerWins) {
    flags.curseNet = -5;
    mods.push({ side: "you", label: "CURSED CIRCUITS — their next unit malfunctions -5" });
  }
  if (eAbility === "metal-witch" && playerWins) {
    flags.curseYou = -5;
    mods.push({ side: "net", label: "CURSED CIRCUITS — your next unit malfunctions -5" });
  }

  /* the core runner's package always arrives */
  if (pAbility === "core-runner" && !playerWins) {
    flags.deliverYou = 3;
    mods.push({ side: "you", label: "CORE DELIVERY — your next unit +3" });
  }
  if (eAbility === "core-runner" && playerWins) {
    flags.deliverNet = 3;
    mods.push({ side: "net", label: "CORE DELIVERY — their next unit +3" });
  }

  /* the hauler delivers the next unit onto the field */
  if (pAbility === "goliath-hauler" && playerWins) {
    flags.transportYou = 4;
    mods.push({ side: "you", label: "MOBILE COMMAND PLATFORM — your next unit +4" });
  }
  if (eAbility === "goliath-hauler" && !playerWins) {
    flags.transportNet = 4;
    mods.push({ side: "net", label: "MOBILE COMMAND PLATFORM — their next unit +4" });
  }

  /* toxic target lock poisons whatever comes next */
  if (pAbility === "mutated-brute" && playerWins) {
    flags.poisonNet = -4;
    mods.push({ side: "you", label: "TOXIC TARGET LOCK — their next unit poisoned -4" });
  }
  if (eAbility === "mutated-brute" && !playerWins) {
    flags.poisonYou = -4;
    mods.push({ side: "net", label: "TOXIC TARGET LOCK — your next unit poisoned -4" });
  }

  /* sector 9's storage banks extra scrap on a win */
  if (pAbility === "sector-9-point" && playerWins) {
    flags.salvage = 3;
    mods.push({ side: "you", label: "SCRAP STORAGE — +3 bonus PTS" });
  }

  /* an overcharge breach scalds its own line when it falls */
  if (pAbility === "overcharged-core" && !playerWins) {
    flags.burnYou = -4;
    mods.push({ side: "you", label: "OVERCHARGE BREACH — your next unit burns -4" });
  }
  if (eAbility === "overcharged-core" && playerWins) {
    flags.burnNet = -4;
    mods.push({ side: "net", label: "OVERCHARGE BREACH — their next unit burns -4" });
  }

  /* the field commander drives the next unit through the breach */
  if (pAbility === "field-commander") {
    flags.hymnYou = 3;
    mods.push({ side: "you", label: "ASSAULT TACTICS — your next unit +3" });
  }
  if (eAbility === "field-commander") {
    flags.hymnNet = 3;
    mods.push({ side: "net", label: "ASSAULT TACTICS — their next unit +3" });
  }

  /* the city runner pockets salvage on a win */
  if (pAbility === "city-runner" && playerWins) {
    flags.salvage = 2;
    mods.push({ side: "you", label: "SALVAGE OP — +2 bonus PTS" });
  }

  /* the chatterer and the data vanguard read the enemy's line */
  if (pAbility === "mutted-chatterer") {
    flags.peekYou = true;
    mods.push({ side: "you", label: "SENSORY FEED — next unit revealed" });
  }
  if (pAbility === "data-vanguard") {
    flags.peekYou = true;
    mods.push({ side: "you", label: "UPLINK SWEEP — next unit revealed" });
  }

  /* spine volley's second strike carries into the next unit */
  if (pAbility === "spine-chimera-assault" && playerWins) {
    flags.rendNet = -2;
    mods.push({ side: "you", label: "SPIKE VOLLEY — their next unit -2" });
  }
  if (eAbility === "spine-chimera-assault" && !playerWins) {
    flags.rendYou = -2;
    mods.push({ side: "net", label: "SPIKE VOLLEY — your next unit -2" });
  }

  /* death burst infects whatever brought the blight-bringer down */
  if (pAbility === "blight-bringer" && !playerWins) {
    flags.curseNet = -5;
    mods.push({ side: "you", label: "DEATH BURST — their next unit infected -5" });
  }
  if (eAbility === "blight-bringer" && playerWins) {
    flags.curseYou = -5;
    mods.push({ side: "net", label: "DEATH BURST — your next unit infected -5" });
  }

  /* omega-7's last stand — the ancient construct wrecks whatever fells it */
  if (pAbility === "omega-7" && !playerWins) {
    flags.curseNet = -8;
    mods.push({ side: "you", label: "LAST STAND — their next unit wrecked -8" });
  }
  if (eAbility === "omega-7" && playerWins) {
    flags.curseYou = -8;
    mods.push({ side: "net", label: "LAST STAND — your next unit wrecked -8" });
  }

  /* the zero-point field detonates when its core falls */
  if (pAbility === "singularity-zero-point" && !playerWins) {
    flags.curseNet = -7;
    mods.push({ side: "you", label: "ZERO-POINT DETONATION — their next unit at -7" });
  }
  if (eAbility === "singularity-zero-point" && playerWins) {
    flags.curseYou = -7;
    mods.push({ side: "net", label: "ZERO-POINT DETONATION — your next unit at -7" });
  }

  /* patch's emergency protocol — her fall buys the next unit a second life */
  if (pAbility === "eliza-patch" && !playerWins) {
    flags.patchYou = 5;
    mods.push({ side: "you", label: "EMERGENCY PROTOCOL — your next unit +5" });
  }
  if (eAbility === "eliza-patch" && playerWins) {
    flags.patchNet = 5;
    mods.push({ side: "net", label: "EMERGENCY PROTOCOL — their next unit +5" });
  }

  /* glitch overclocks the line when she wins */
  if (pAbility === "maeve-glitch" && playerWins) {
    flags.hymnYou = 3;
    mods.push({ side: "you", label: "SYSTEM OVERCLOCK — your next unit +3" });
  }
  if (eAbility === "maeve-glitch" && !playerWins) {
    flags.hymnNet = 3;
    mods.push({ side: "net", label: "SYSTEM OVERCLOCK — their next unit +3" });
  }

  /* surge burns out — if he falls, the drain hits your next unit */
  if (pAbility === "jax-surge" && !playerWins) {
    flags.strainYou = -3;
    mods.push({ side: "you", label: "OVERLOAD BURNOUT — your next unit -3" });
  }
  if (eAbility === "jax-surge" && playerWins) {
    flags.strainNet = -3;
    mods.push({ side: "net", label: "OVERLOAD BURNOUT — their next unit -3" });
  }

  /* the scavenger king always finds something worth keeping */
  if (pAbility === "elias-scrap" && playerWins) {
    flags.salvage = 2;
    mods.push({ side: "you", label: "SCAVENGER'S INSTINCT — +2 bonus PTS" });
  }

  /* the grid runner's overclock carries the next unit forward */
  if (pAbility === "kenji-shift" && playerWins) {
    flags.hymnYou = 2;
    mods.push({ side: "you", label: "SYSTEM OVERCLOCK — your next unit +2" });
  }
  if (eAbility === "kenji-shift" && !playerWins) {
    flags.hymnNet = 2;
    mods.push({ side: "net", label: "SYSTEM OVERCLOCK — their next unit +2" });
  }

  /* caleb strips the field bare — salvage scales with the war's length */
  if (pAbility === "caleb-forge" && playerWins) {
    flags.salvage = 1 + ctx.roundIndex;
    mods.push({ side: "you", label: `SCAVENGED SALVAGE — +${1 + ctx.roundIndex} bonus PTS` });
  }

  /* lyra's drone marks the prey and punches through plating */
  if (pAbility === "lyra-shadow") {
    flags.peekYou = true;
    flags.poisonNet = -2;
    mods.push({ side: "you", label: "SCOUT'S EYE — next unit revealed, marked -2" });
  }
  if (eAbility === "lyra-shadow") {
    flags.poisonYou = -2;
    mods.push({ side: "net", label: "SCOUT'S EYE — your next unit marked -2" });
  }

  return { pTotal, eTotal, pRoll, eRoll, pEdge, eEdge, playerWins, mods, flags };
};

/* ---------- pack crate ---------- */
export const PACK_COST = 60;
export const PACK_SIZE = 3;

const PACK_POOLS = {
  common: ["net-warden", "infantry", "scavenger", "vex", "sector-9-lockdown", "scavenger-elite", "city-runner"],
  rare: ["kaelen", "zion", "orion", "dolt", "dolo", "kishuu", "luna", "malfunction-7", "core-runner", "mutated-brute", "sector-9-point", "sector-defense-bastion", "white-guard-reinforcement", "scrapper-elite-technician", "ironclad", "field-commander", "data-vanguard", "vanguard-scrapper-elite", "data-elite", "eye-clusters", "mutted-chatterer", "mutted-chatterer-internal", "eliza-patch", "maeve-glitch", "jax-surge", "kara-raven", "zoe-spark", "elias-scrap", "kenji-shift", "eliza-maw", "caleb-forge", "lyra-shadow", "eye-clusters-ocular", "mutted-chatterer-cortex", "abyss", "cyber-ape", "kai-vagabond"],
  legendary: ["drion", "valkyrie", "net-commander", "phantom", "kushiro", "vanguard", "echo", "volo", "singularity", "omnicron", "cyber-tyrant", "circe", "deacon", "funk-master", "goji", "dark-dolo", "kai", "jonah", "sarah", "kenjiro", "gorge-claw", "sinew-walker", "ash-skin", "energy-eater", "sand-worm", "osteowarrior", "metal-witch", "cyborg-shogun", "riff", "koji", "sniper-elite", "heavy-enforcer", "goliath", "elara", "white-guard-bastion", "command-bastion", "goliath-hauler", "siege-mutant", "chronos-chimera", "assimilation-abomination", "riotball-final", "red-force-pitch", "assimilation-lord", "overcharged-core", "bio-assimilator", "spine-chimera", "rust-eater", "spine-chimera-assault", "shadow-kind", "scale-hide", "blight-bringer", "kane-ironhide", "chimera-lord-vance", "omega-7", "eye-clusters-psionic", "architect", "cyber-queen", "omnicron-nexus", "singularity-zero-point", "nexus-omnicron", "elara-scout", "rez-waste-bearer", "sector-9-lockdown-perimeter", "sector-9-chaos-engine", "cybernetic-revival-chamber", "cybernetic-repair-bay", "cybernetic-archive-vault", "cybernetic-arsenal-forge", "cybernetic-refinery-complex", "cybernetic-data-spy-network", "omega-protocol", "scavengers-guild", "technomancers-cabal", "nexus-convergence", "nexus-prime-core", "cybernetic-veterans-legion", "chronos-vortex", "synthetic-ascension-core", "cyber-psionic-matrix", "omega-project", "chrono-steward-aethel", "chrono-keeper-elara", "chrono-architect-kaelen", "chrono-lord-kaeleb"],
};

const rollRarity = () => {
  const roll = Math.random() * 100;
  if (roll < 50) return "common";
  if (roll < 85) return "rare";
  return "legendary";
};

/* guest-side draw, mirrors the server table */
export const drawPackLocal = () =>
  Array.from({ length: PACK_SIZE }, () => {
    const pool = PACK_POOLS[rollRarity()];
    return pool[Math.floor(Math.random() * pool.length)];
  });

export const getLocalCollection = () => {
  try {
    return JSON.parse(localStorage.getItem(COLLECTION_KEY)) || {};
  } catch {
    return {};
  }
};

export const recordPullsLocal = (ids) => {
  const collection = getLocalCollection();
  ids.forEach((id) => {
    collection[id] = (collection[id] || 0) + 1;
  });
  localStorage.setItem(COLLECTION_KEY, JSON.stringify(collection));
  return collection;
};

export const fetchCloudCollection = async () => {
  const tag = getLinkedTag();
  if (!tag) return null;
  try {
    const { data } = await axios.get(`${API}/tcg/collection/${encodeURIComponent(tag)}`);
    return data;
  } catch {
    return null;
  }
};

/* Crack one crate. Linked tags go through the server (authoritative spend +
   collection); guests draw locally. Pass useWeekly to fire the half-price
   weekly supply drop. Returns { cards, cost }. Throws with a readable
   detail when the crate stays shut. */
export const openPack = async (useWeekly = false) => {
  const tag = getLinkedTag();
  if (tag) {
    try {
      const { data } = await axios.post(`${API}/tcg/pack/${encodeURIComponent(tag)}`, { use_weekly: useWeekly });
      overrideLocalPoints(data.points);
      recordPullsLocal(data.cards);
      return { cards: data.cards, cost: data.cost };
    } catch (error) {
      if (error.response) throw new Error(error.response.data?.detail || "VAULT FAULT — CRATE HELD. RETRY.");
      /* true network hiccup — fall through to the local draw */
    }
  }
  const cost = useWeekly ? WEEKLY_COST : PACK_COST;
  if (!spendLocalPoints(cost)) throw new Error(`STASH TOO THIN — ${cost} PTS NEEDED. RUN THE GAMES.`);
  if (useWeekly) claimWeeklyLocal();
  const cards = drawPackLocal();
  recordPullsLocal(cards);
  return { cards, cost };
};

/* ---------- dupe shredding ---------- */
export const SHRED_COPIES = 3;
export const SHRED_VALUES = { common: 12, rare: 18, legendary: 30 };

export const shredValueFor = (id) => SHRED_VALUES[cardById(id)?.rarity] || 0;

const shredLocal = (id) => {
  const collection = getLocalCollection();
  if ((collection[id] || 0) < SHRED_COPIES) return null;
  collection[id] -= SHRED_COPIES;
  localStorage.setItem(COLLECTION_KEY, JSON.stringify(collection));
  return collection[id];
};

/* Melt 3 copies of one unit into a PTS refund. Linked tags shred server-side
   (authoritative balance + collection); guests melt the local stash. */
export const shredCard = async (id) => {
  const refund = shredValueFor(id);
  const tag = getLinkedTag();
  if (tag) {
    try {
      const { data } = await axios.post(`${API}/tcg/shred/${encodeURIComponent(tag)}`, { card: id });
      overrideLocalPoints(data.points);
      shredLocal(id);
      return data;
    } catch (error) {
      if (error.response?.status === 400) throw new Error(error.response.data?.detail || "SHRED REJECTED.");
      if (error.response) throw new Error("VAULT FAULT — NOTHING MELTED. RETRY.");
      /* true network hiccup (no response) — fall through to the local melt */
    }
  }
  const remaining = shredLocal(id);
  if (remaining === null) throw new Error(`SHRED REJECTED — ${SHRED_COPIES} COPIES REQUIRED TO MELT.`);
  overrideLocalPoints(getLocalPoints() + refund);
  return { points: getLocalPoints(), remaining, refund };
};

/* ---------- weekly supply drop — one half-price crate per week ---------- */
export const WEEKLY_COST = 30;

export const currentIsoWeek = () => {
  const now = new Date();
  const utc = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const day = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((utc - yearStart) / 86400000 + 1) / 7);
  return `${utc.getUTCFullYear()}W${String(week).padStart(2, "0")}`;
};

const WEEKLY_KEY = "dolo-tcg-weekly";

/* is this week's half-price crate still unclaimed? cloud week wins for linked tags */
export const weeklyDropAvailable = (cloudWeek) => {
  const week = currentIsoWeek();
  if (getLinkedTag()) return cloudWeek !== week;
  return localStorage.getItem(WEEKLY_KEY) !== week;
};

const claimWeeklyLocal = () => localStorage.setItem(WEEKLY_KEY, currentIsoWeek());

/* ---------- trade board — swap duplicate pulls between runners ---------- */
export const fetchTrades = async () => {
  const { data } = await axios.get(`${API}/tcg/trades`);
  return data;
};

export const postTrade = async (give, want) => {
  const tag = getLinkedTag();
  if (!tag) throw new Error("LINK A RUNNER TAG TO TRADE — SIGN IN AT ACCOUNT.");
  try {
    const { data } = await axios.post(`${API}/tcg/trades`, { tag, give, want });
    return data.trade;
  } catch (error) {
    throw new Error(error.response?.data?.detail || "TRADE REJECTED.");
  }
};

export const acceptTrade = async (tradeId) => {
  const tag = getLinkedTag();
  if (!tag) throw new Error("LINK A RUNNER TAG TO TRADE — SIGN IN AT ACCOUNT.");
  try {
    const { data } = await axios.post(`${API}/tcg/trades/${tradeId}/accept`, { tag });
    /* keep the local collection mirror honest */
    const collection = getLocalCollection();
    collection[data.sent] = (collection[data.sent] || 1) - 1;
    collection[data.received] = (collection[data.received] || 0) + 1;
    localStorage.setItem(COLLECTION_KEY, JSON.stringify(collection));
    return data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || "TRADE FAILED.");
  }
};

export const cancelTrade = async (tradeId) => {
  const tag = getLinkedTag();
  if (!tag) throw new Error("LINK A RUNNER TAG TO TRADE — SIGN IN AT ACCOUNT.");
  try {
    await axios.delete(`${API}/tcg/trades/${tradeId}`, { data: { tag } });
  } catch (error) {
    throw new Error(error.response?.data?.detail || "CANCEL FAILED.");
  }
};

/* ---------- weekly boss rotation — a different commander hunts each ISO week ---------- */
export const WEEKLY_BOSSES = [
  {
    id: "omega-7",
    title: "OMEGA-7'S FINAL SENTINEL PROTOCOL",
    short: "FINAL SENTINEL",
    pronoun: "ITS",
    bounty: "OMEGA-7, THE FINAL SENTINEL",
    /* the mutant vanguard first, the Final Sentinel closes */
    deck: ["eye-clusters-ocular", "mutted-chatterer-cortex", "scale-hide", "eye-clusters-psionic", "bio-assimilator", "omega-7"],
  },
  {
    id: "architect",
    title: "ARCHITECT'S VOID-NET EXHIBITION",
    short: "VOID-NET WEAVER",
    pronoun: "ITS",
    bounty: "ARCHITECT, THE VOID-NET WEAVER",
    /* the net's rank and file, then the Weaver itself */
    deck: ["net-warden", "net-commander", "abyss", "sector-9-point", "omnicron-nexus", "architect"],
  },
  {
    id: "cyber-queen",
    title: "CYBER-QUEEN'S NET-MATRIX COURT",
    short: "NET-MATRIX SOVEREIGN",
    pronoun: "HER",
    bounty: "CYBER-QUEEN, THE NET-MATRIX SOVEREIGN",
    /* the court's bastions hold the line until the Sovereign takes the field */
    deck: ["net-warden", "abyss", "sector-9-point", "sector-defense-bastion", "omnicron-nexus", "cyber-queen"],
  },
  {
    id: "super-ronin-dolo",
    title: "SUPER RONIN DOLO'S IRON RESOLVE GAUNTLET",
    short: "IRON RONIN",
    pronoun: "ITS",
    bounty: "SUPER RONIN DOLO, THE IRON RONIN",
    /* the vanguard's iron wall first, the Ronin closes */
    deck: ["field-commander", "ironclad", "kane-ironhide", "cyborg-shogun", "goji", "super-ronin-dolo"],
  },
  {
    id: "vinyl-valkyrie",
    title: "THE VINYL-VALKYRIE'S BASS-DROP SYMPHONY",
    short: "BASS-DROP QUEEN",
    pronoun: "HER",
    bounty: "THE VINYL-VALKYRIE, QUEEN OF THE BASS-DROP",
    /* the sound system warms up before the Queen drops the bass */
    deck: ["funk-master", "riff", "koji", "metal-witch", "energy-eater", "vinyl-valkyrie"],
  },
  {
    id: "tape-worm",
    title: "THE TAPE-WORM'S MAGNETIC COIL CACHE",
    short: "MAGNETIC COIL",
    pronoun: "ITS",
    bounty: "THE TAPE-WORM, THE MAGNETIC COIL",
    /* corrupted data units twitch before the Worm uncoils */
    deck: ["abyss", "data-vanguard", "data-elite", "omnicron-nexus", "singularity", "tape-worm"],
  },
  {
    id: "last-hero",
    title: "THE LAST HERO'S ECHO OF HOPE",
    short: "ECHO OF HOPE",
    pronoun: "HIS",
    bounty: "THE LAST HERO, BEACON OF THE OLD WORLD",
    /* the old world's defenders rally before the Hero stands alone */
    deck: ["city-runner", "scavenger-elite", "white-guard-reinforcement", "vanguard", "elara", "last-hero"],
  },
  {
    id: "synth-ronin",
    title: "THE SYNTH-RONIN'S CIRCUIT SHADOW DRIFT",
    short: "CIRCUIT SHADOW",
    pronoun: "ITS",
    bounty: "THE SYNTH-RONIN, THE CIRCUIT SHADOW",
    /* the net's phantoms phase in before the Ronin draws */
    deck: ["net-warden", "phantom", "omnicron", "nexus-omnicron", "singularity-zero-point", "synth-ronin"],
  },
];

/* backend mirrors this rotation in server.py WEEKLY_BOSS_CARDS — keep the order identical */
export const bossOfWeek = (week = currentIsoWeek()) => {
  const match = week.match(/(\d+)W(\d+)/);
  const index = match ? (Number(match[1]) * 100 + Number(match[2])) % WEEKLY_BOSSES.length : 0;
  return WEEKLY_BOSSES[index];
};

export const BOSS_ID = bossOfWeek().id;
export const BOSS_DECK_IDS = bossOfWeek().deck;

export const buildBossDeck = () =>
  BOSS_DECK_IDS.map(cardById).filter(Boolean).sort((a, b) => cardPower(a) - cardPower(b));

const BOSS_WEEK_KEY = "dolo-tcg-boss-week";

export const bossClaimedThisWeek = (cloudWeek) => {
  const week = currentIsoWeek();
  if (getLinkedTag()) return cloudWeek === week;
  return localStorage.getItem(BOSS_WEEK_KEY) === week;
};

/* claim the boss card after a win — once per ISO week; guests bank locally */
export const claimBossWin = async () => {
  const tag = getLinkedTag();
  if (tag) {
    const { data } = await axios.post(`${API}/tcg/boss/claim`, { tag });
    recordPullsLocal([data?.card || BOSS_ID]);
    return data;
  }
  localStorage.setItem(BOSS_WEEK_KEY, currentIsoWeek());
  recordPullsLocal([BOSS_ID]);
  return { success: true, card: BOSS_ID };
};

/* ---------- trade notifications — accepted offers flag on return ---------- */
export const fetchTradeNotifications = async () => {
  const tag = getLinkedTag();
  if (!tag) return [];
  try {
    const { data } = await axios.get(`${API}/tcg/notifications/${encodeURIComponent(tag)}`);
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
};
